import { Injectable } from '@angular/core';
import { SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { Observable, Subject  } from 'rxjs';

import { LMSavedSearchComponent, LMSavedSearchManageComponent } from '.';
import { LMDialogButtonKey, LMUniqueIdService, LMGlobalUtilService, AppConstants } from '@utils/index';
import {
  LMUrlUtilService,
  LMConfigurationService,
  LMSpecService,
  LMFormatUtilService,
  LMLoggerService, LMErrorUtilService, LMContextService, LMBusyIndicatorService
} from '@services/index';
import { KeyedResource } from '@app/resources';
import { first } from 'rxjs/operators';
import { LMActionFormDialogResultCode, LMActionFormDialogService } from '@app/components';
import { LMTransactionState } from '@app/transaction';
import * as urltemplate from 'url-template';

@Injectable({
  providedIn: 'root'
})
export class LMSavedSearchService {

  constructor(
    private sohoModalDialogService: SohoModalDialogService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmConfigurationService: LMConfigurationService,
    private lmSpecService: LMSpecService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmLoggerService: LMLoggerService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmActionFormDialogService: LMActionFormDialogService,
    private lmContextService: LMContextService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
  ) {}

  private getDataView(userListViewParams: LMUserListViewParams): LMDataView {
    const fields = {
      Name: { value: userListViewParams.userListViewItem?.name },
      Description: { value: userListViewParams.userListViewItem?.description },
      IsDefault: { value: userListViewParams.userListViewItem?.isDefault },
      SearchState: { value: JSON.stringify(userListViewParams.searchState) },
      BusinessView: { value: userListViewParams.listSpec.busClassSpec.name },
      BusinessList: { value: userListViewParams.listSpec.busListSpec.name }
    } as LMFields;
    if (userListViewParams.userListViewItem?.userListView) {
      fields['UserListView'] = { value: userListViewParams.userListViewItem.userListView };
    }
    // Existing saved searches do not have the following fields defined.
    // DataUtils throws a runtime exception if field json does not have value defined.
    if (!fields['Description'].value) {
      fields['Description'].value = '';
    }
    if (!fields['IsDefault'].value) {
      fields['IsDefault'].value = false;
    }

    const dvResourceId = userListViewParams.userListViewItem
      ? new KeyedResource({ resourceType: 'form', resourceId: 'UserListView(' + userListViewParams.userListViewItem.userListView + ')' })
      : new KeyedResource({ resourceType: 'form', resourceId: 'UserListView(_niu_)' });
    return {
      action: (userListViewParams.userListViewItem) ? 'Update' : 'Create',
      fields,
      resourceId: dvResourceId.generateResourceId(true)
    } as LMDataView;
  }

  private showToast(success: boolean) {
    const title = success ? Soho.Locale.translate('SaveCompleted') : Soho.Locale.translate('SaveFailed');
    const message = success ? Soho.Locale.translate('CurrentSearchIsSaved') : Soho.Locale.translate('CurrentSearchIsNotSaved');
    this.lmGlobalUtilService.displayToastMessage(title, message);
  }

  private save(userListViewParams: LMUserListViewParams): Observable<LMUserListViewResult> {
    const dataView = this.getDataView(userListViewParams);
    const url = this.lmUrlUtilService.buildUserListViewFetchUrl(userListViewParams.listUrlInfo, userListViewParams.listSpec);
    return new Observable<LMUserListViewResult>((observer) => {
      this.lmConfigurationService.saveUserListView(url, dataView).subscribe((response: LMActionResult) => {
        this.showToast(AppConstants.STATUS_COMPLETED === response.status);
        const userListViewItem: LMUserListViewItem = {
          userListView: response.dataView.fields['UserListView'].value as string,
          name: response.dataView.fields['Name'].value as string,
          description: response.dataView.fields['Description'].value as string,
          isDefault: response.dataView.fields['IsDefault'].value as boolean,
          searchState: response.dataView.fields['SearchState'].value as string,
        };
        observer.next({ success: AppConstants.STATUS_COMPLETED === response.status,
          userListViewItem });
        observer.complete();
      });
    });
  }

  public runSave(userListViewParams: LMUserListViewParams): Observable<LMUserListViewResult> {
    if (userListViewParams.userListViewItem !== undefined) {
      // Just save it, taking in the modifications.
      return this.save(userListViewParams);
    } else {
      // Open the dialog to save
      return this.openSaveActionDialog(userListViewParams);
    }
  }

  public runSaveAs(userListViewParams: LMUserListViewParams): Observable<LMUserListViewResult> {
    if (userListViewParams.name) {
      userListViewParams.name = userListViewParams.name + '-Copy';
    }
    // Always prompt a dialog to enter a name
    return this.openSaveActionDialog(userListViewParams);
  }

  private openSaveActionDialog(userListViewParams: LMUserListViewParams): Observable<LMUserListViewResult> {
    const sub = new Subject<LMUserListViewResult>();

    this.lmBusyIndicatorService.showBusyIndicator();
    const resource = new KeyedResource({ resourceType: 'form', resourceId: 'UserListView(_niu_)' });
    const createAction = 'SaveUserListView';
    const actionResource = new KeyedResource( { resourceType: 'action', resourceId: resource.resourceId }, createAction);
    const params = { output: 'spec' };
    const url = this.lmUrlUtilService.buildUrl({ resource: actionResource, params });

    this.lmSpecService.getFormSpecFromRedirectActionUrl(url).pipe(first())
      .subscribe((response: LMActionFormUrlResponse | LMSystemException) => {
        if ((response as LMSystemException).status
          && ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
            || (response as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
          const exception = response as LMSystemException;
          this.lmLoggerService.error(response as LMSystemException);
          this.lmErrorUtilService.handleError(exception.message, exception.details, exception.status, exception.statusText);
        }
        const actionFormUrlResponse = response as LMActionFormUrlResponse;
        const formUrlInfo = actionFormUrlResponse.formUrlInfo;
        const formSpec = actionFormUrlResponse.formSpec;
        const actionSpec = formSpec.busClassSpec.actions.find((a) => a.name === createAction);
        const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
        const dataUrl = urlCompiledTemplate.expand({ resourceId: resource.resourceId, actionName: actionSpec.name });
        this.lmActionFormDialogService.getActionFormDialogDataView(dataUrl).pipe(first()).subscribe((responseDV: any) => {
          const formDataView: LMDataView = responseDV;
          if (formDataView.status !== AppConstants.STATUS_COMPLETED) {
            this.lmLoggerService.error(responseDV);
            this.lmErrorUtilService.handleError(responseDV.message, responseDV.details, responseDV.status, responseDV.statusText);
            return;
          }
          this.setFieldsOnCreateDataView(userListViewParams, formDataView);
          this.lmActionFormDialogService.runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView)
            .subscribe((actionFormDialogResult) => {
              if (actionFormDialogResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
                const transactionState: LMTransactionState = actionFormDialogResult.transactionStateResult;
                if (transactionState.isSuccess()) {
                  const actionResult: LMActionResult = actionFormDialogResult.transactionStateResult.getTransactionResult();
                  const resultDataView = actionResult.dataView;
                  const userListViewItem: LMUserListViewItem = {
                    userListView: resultDataView.fields[ 'UserListView' ].value as string,
                    name: resultDataView.fields[ 'Name' ].value as string,
                    description: resultDataView.fields[ 'Description' ].value as string,
                    isDefault: resultDataView.fields[ 'IsDefault' ].value as boolean,
                    searchState: resultDataView.fields[ 'SearchState' ].value as string,
                  };
                  const result: LMUserListViewResult = { success: true, userListViewItem, dataView: resultDataView };
                  sub.next(result);
                  this.showToast(true);
                }
              } else if (actionFormDialogResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR) {
                this.lmBusyIndicatorService.hideBusyIndicator();
                this.lmErrorUtilService.handleError(actionFormDialogResult.openError.message, actionFormDialogResult.openError.details, actionFormDialogResult.openError.status);
              }
            });
        });
      });
    return sub.asObservable();
  }

  private setFieldsOnCreateDataView(userListViewParams: LMUserListViewParams, dataView: LMDataView) {
    dataView.fields[ 'BusinessView' ].value = userListViewParams.listSpec.busClassSpec.name;
    dataView.fields[ 'BusinessList'].value = userListViewParams.listSpec.busListSpec.name;
    dataView.fields[ 'Actor' ].value = this.lmContextService.getActorId();
    if (userListViewParams.listSpec.userFolder) {
      dataView.fields[ 'UserFolder' ].value = userListViewParams.listSpec.userFolder;
    }
    if (userListViewParams.name) {
      dataView.fields[ 'Name' ].value = userListViewParams.name;
    }
    dataView.fields[ 'SearchState' ].value = JSON.stringify(userListViewParams.searchState);
    dataView.fields[ 'NewDisplayOrder' ].value = 1;
  }

  public openManageDialog(businessClass: string, listName: string, listTitle: string, actorId: string, userFolder: string): Observable<boolean> {
    const params = { pagesize: 5 };
    // const params = { pagesize: 5, 'fc.BusinessView' : 'contains(' + businessClass + ')', 'fc.BusinessList' : 'contains(' + listName + ')' };
    const listResource = new KeyedResource({ resourceType: 'list',
      resourceId: 'UserListView[ByViewListActor](' + businessClass + ',' + listName + ',' + actorId + ',' + userFolder + ').UserListViewQuickManageList' });
    const url = this.lmUrlUtilService.buildUrl({ resource: listResource, params });
    const dialogId = 'SavedSearchManageDialog';
    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: 'lm-manage-saved-search-modal'
    } ];
    const dialog = this.sohoModalDialogService.modal(LMSavedSearchManageComponent);

    this.lmSpecService.getListSpec(url).subscribe((listSpecResponse: LMListSpecResponse ) => {
      dialog.title(this.lmFormatUtilService.formatMessage(Soho.Locale.translate('ManageSavedSearch'), [ listTitle ]))
        .options({ fullsize: 'always', attributes, cssClass: 'lm-saved-search-manage' })
        .buttons([
          {
            id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CLOSE ]),
            text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
            click: () => dialog.close(false),
            isDefault: true
          },
        ])
        .apply((manageComponent: LMSavedSearchManageComponent) => {
          manageComponent.listSpec = listSpecResponse.listSpec;
          manageComponent.listUrlInfo = listSpecResponse.listUrlInfo;
        })
        .open();
    });

    const sub = new Subject<boolean>();
    dialog.afterClose((result: boolean) => {
      sub.next(result);
      sub.complete();
    });
    return sub.asObservable();

  }
}
